import { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import { fetchAddress } from '../../services/locationInfoService';
import { getCurrentLocationCoords } from '../../services/currentLocationService';
import config from "../../config/config";

const BFF_API_BASE_URL = config.api.bff.baseUrl;

const NaviFrom = forwardRef((
  { formType, map, formData,  setFormData, setResultError, naviSearch, setNaviSearch, driveHighwayType, isShowNavi}
  , ref
) => {
  const [timeoutId, setTimeoutId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const suggestWaitTime = 300; // サジェストが開始されるまでのウェイト ms
  const [forceSearchCount, setForceSearchCount] = useState(0);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isDuaringCurrentPosition, setIsDuaringCurrentPosition] = useState(false);
  const itemRefs = useRef([]); // リストアイテムのDOM要素を参照するためのRef配列
  const $ = window.$;
  let [marker, setMarker] = useState(null);

  useImperativeHandle(ref, () => ({
    setDestination: setDestination,
    setCurrentLocation: setCurrentLocation
  }));

  useEffect(() => {
    return () => {
      // アンマウント時に経由地の場合はこのNaviFromが削除されるので意図的にマーカーを消しておく
      if(marker && formType === "via") marker.remove();
    };
  }, [marker, formType]);

  const markerDragEnd = (e)=>{
    let {target} = e; // targetはドラッグしたmarkerインスタンスそのもの
    marker = target;
    const {lng, lat} = target.getLngLat();
    setResultError("");
    setFormData({ name: null, lat: null, lon: null });

    // リバースジオコーディングAPIを呼び出して住所を取得
    fetchAddress(lng, lat)
      .then((data) => {
        let address = '住所不明';
        if (data?.value?.body?.where?.full?.address) {
          address = data.value.body.where.full.address;
        }
        setFormData({ name: address, lat: lat, lon: lng, marker});
        setMarker(marker);
      })
      .catch(error => {
        console.error('Error fetching address:', error);
      });
  }

  const searchSuggest = (e) => {
    clearTimeout(timeoutId);
    if(formData.marker) formData.marker.remove(); // 表示されたマーカーを削除する
    setResultError("")
    setFormData({ name: e.target.value, lat: null, lon: null, marker: null })

    let timeout = setTimeout(() => { 
      const query = e.target.value;
      if (query.length < 2) {
        // 2文字未満なら何もしない
        return;
      }

    // サジェストAPIを呼び出す
    let {lat, lng} = map.getCenter();
    fetch(`${BFF_API_BASE_URL}/suggest?q=${encodeURIComponent(query)}&lat=${lat}&lon=${lng}`)
      .then(response => response.json())
      .then(({data}) => {
        // サジェスト結果を状態にセット
        setSuggestions(data.items || []);
        setHighlightedIndex(0); // サジェストの位置は先頭にしておく
      })
      .catch(error => {
        console.error('Error fetching suggestions:', error);
      });
      
    }, suggestWaitTime);
    setTimeoutId(timeout);

  };

  const searchSuggestFirstSet = (name) => {
    clearTimeout(timeoutId);
    if(formData.marker) formData.marker.remove(); // 表示されたマーカーを削除する
    
    setResultError("")

    let timeout = setTimeout(() => { 
    // サジェストAPIを呼び出す
    let {lat, lng} = map.getCenter();
    fetch(`${BFF_API_BASE_URL}/suggest?q=${encodeURIComponent(name)}&lat=${lat}&lon=${lng}`)
      .then(response => response.json())
      .then(({data}) => {
        // サジェスト結果を状態にセット
        let [firstData] = data.items;
        setDestination(firstData);
      })
      .catch(error => {
        console.error('Error fetching suggestions:', error);
      });
      
    }, suggestWaitTime);
    setTimeoutId(timeout);
  }

  // フォームの行き先を決定する
  const setDestination = ({name, poi_name, lat, lon}) => {
    if(formData.marker) formData.marker.remove(); // 表示されたマーカーがあったら削除する
    const finalName = name || poi_name || '';
    setSuggestions([]); // サジェストをクリア
    setResultError(""); // エラーもクリア

    if(!lat && !lon){ // 緯度経度が存在しない場合はmarkerを設置せずアーリリターン
      setFormData({ name: finalName, lat, lon });
      return ;
    }

    // start or endに設定
    // マーカーを描画する
    marker =  new window.maplibregl.Marker({draggable: true})
    marker.setLngLat([lon, lat]).addTo(map); // 発・着マーカーの緯度経度をセット
    marker.on("dragend", markerDragEnd);
    let $markerElm = $(marker.getElement());
    let iconImg = {
      start: "https://www.mapion.co.jp/f/mapion_map/img/navi/navi_icon_start.png",
      end: "https://www.mapion.co.jp/f/mapion_map/img/navi/navi_icon_end.png",
      via: "https://www.mapion.co.jp/f/mapion_map/img/navi/navi_icon_via.png"
    }[formType]
    $markerElm.html(`<img src="${iconImg}">`)

    // TODO: startが決まってる時にendを決めるとendに移動して、全体最適縮尺になるのでちょっと工夫が必要
    map.flyTo({center: [lon, lat]});
    setFormData({ name: finalName, lat, lon, marker });
    setMarker(marker);
  }
 
  // 現在地から設定
  const setCurrentLocation = () => {
    if(isDuaringCurrentPosition) return; // 現在地取得中は何もしない
    setIsDuaringCurrentPosition(true);
    getCurrentLocationCoords()
      .then(({lat, lon})=>{
        // リバースジオコーディングAPIを呼び出して住所を取得
        fetchAddress(lon, lat)
          .then((data) => {
            let address = '住所不明';
            if (data?.value?.body?.where?.full?.address) {
              address = data.value.body.where.full.address;
            }
            setDestination({ name: address, lat, lon});
            setNaviSearch({...naviSearch, reSearchFlag: true})
            setIsDuaringCurrentPosition(false);
          })
          .catch(error => {
            console.error('Error fetching address:', error);
          });
      })
      .catch((error)=>{
        console.error('Error getting current location:', error);
      })
  };

  const deleteNaviWord = (e)=>{
    let {currentTarget} = e;
    let $ = window.$;

    if(formData.marker) formData.marker.remove(); // 表示されたマーカーを削除する
    setSuggestions([]); // サジェストをクリア

    if(formType === "via" && !formData.name){
      // 経由地かつ入力内容がnameが空だったら何もしない ※delForClearBtn フラグをたてずに再検索させないことが目的
    } else {
      setFormData({ name: null, lat: null, lon: null, marker: null, delForClearBtn: true});
    }
    $(currentTarget).siblings("form").find("input").val(""); // reactのinputはnameをnullにしても消えないので能動的にinputの内容を消す
  }

  const iconClass = formType;
  let label = "出発地";
  if(formType === "end"){
    label = '目的地';
  } else if(formType === "via"){
    label = '経由地';
  }

  const setHighwayCheck = (e) => {
    naviSearch.drive[driveHighwayType] = naviSearch.drive[driveHighwayType] === 2? 1: 2;
    setNaviSearch({...naviSearch, drive: {...naviSearch.drive}})
  }

  const setMapCenter = (e) => {
    e.preventDefault();
    let {lat, lng} = map.getCenter();
    // リバースジオコーディングAPIを呼び出して住所を取得
    fetchAddress(lng, lat)
      .then((data) => {
        let address = '住所不明';
        if (data?.value?.body?.where?.full?.address) {
          address = data.value.body.where.full.address;
        }
        setDestination({ name: address, lat: lat, lon: lng});
      })
      .catch(error => {
        console.error('Error fetching address:', error);
      });
  }

  const setForLatLng = (lat, lng) => {
    // リバースジオコーディングAPIを呼び出して住所を取得
    fetchAddress(lng, lat)
      .then((data) => {
        let address = '住所不明';
        if (data?.value?.body?.where?.full?.address) {
          address = data.value.body.where.full.address;
        }
        setDestination({ name: address, lat: lat, lon: lng});
      })
      .catch(error => {
        console.error('Error fetching address:', error);
      });
  }

  const handleKeyDown = (e) => {
    if ( e.isComposing || e.key === 'Process' || e.keyCode === 229) return; // 日本語変換中なので無視する

    if(suggestions.length !== 0){
       // キーを正規化（Ctrl+N → ArrowDown相当、Ctrl+P → ArrowUp相当として扱う）
      let normalizedKey = e.key;
      if (e.ctrlKey && e.key.toLowerCase() === 'n') normalizedKey = 'ArrowDown';
      if (e.ctrlKey && e.key.toLowerCase() === 'p') normalizedKey = 'ArrowUp';

      switch (normalizedKey) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : prev
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          setDestination(suggestions[highlightedIndex]);

        }
        break;
      case 'Escape':
        setSuggestions([]); // サジェストをクリア
        break;
      default:
        break;
    }
    }
  }

  // 選択インデックスが変わったらスクロールさせる
  useEffect(() => {
    // 有効なインデックスかつ、参照が存在する場合のみ実行
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex].scrollIntoView({
        // behavior: 'smooth', // スムーズにスクロール
        block: 'nearest',   // 見えている範囲からはみ出した時だけスクロール
      });
    }
  }, [highlightedIndex]);

  let {type} = naviSearch;
  let {name, lat, lon, forceSearch} = formData;

  // ナビが表示、かつ、検索できる状態の場合はマーカーを再表示する
  if(isShowNavi && (name && lat && lon && marker)){
    marker.addTo(map)
  } else if(isShowNavi && (name && lat && lon)) {
    // ナビが表示、かつ、経由地の場合は検索ができる状態の場合はマーカーの設定をする
    setDestination({name, lat, lon});
  }else if(!isShowNavi){
    // ナビが非表示のときはマーカーを削除する ※startとendが対象
    if(marker) marker.remove();
  } else if ((name || (lat && lon)) && forceSearch && forceSearchCount ===0){
    setFormData({ name: name, lat: null, lon: null, forceSearch: false })
    // パラメータから強制的に検索のフラグがたってる場合は、
    // サジェスト検索し、先頭のデータをセットする
    setForceSearchCount(forceSearchCount + 1);
  
    if(name){
      searchSuggestFirstSet(name);
    } else if(lat && lon){
      setForLatLng(lat, lon);
    }
    
  }

  return (
    <div>
      <div className="route-input-group">
        <div className={`route-point-indicator ${iconClass}`}>
            <i className="svg-icon-white" style={{ backgroundImage: `url('assets/icons/location_24.svg')` }}></i>
        </div>
        <form className="route-input-form" onSubmit={(e) =>{e.preventDefault()}}>
          <input type="text" className="route-input typeahead" id="start-point" autocomplete="off"
              placeholder={label + "を入力"} aria-label={label} value={formData.name}
              onChange={searchSuggest} onKeyDown={handleKeyDown} />
        </form>
        {suggestions.length > 0 && (
          <ul className="suggestions-list">
            {suggestions.map((item, index) => (
              <li key={index} onClick={() => {setDestination(item)}}
                style={{background: index === highlightedIndex ? '#e3f2fd' : 'white'}}
                // ref callback を使って配列にDOMを格納
                ref={el => itemRefs.current[index] = el}
              >
                {item.name || item.poi_name}
              </li>
            ))}
          </ul>
        )}
        <button className="navi-form-reset-btn" title="検索ワードの削除" onClick={deleteNaviWord} >
          <i className="svg-icon-blue" style={{ backgroundImage: `url('assets/icons/close_24.svg')` }}></i>
        </button>
        <button className="current-location-btn" title="現在地を設定" onClick={setCurrentLocation} >
            <i className="svg-icon-blue" style={{ backgroundImage: `url('assets/icons/location_searching_24.svg')` }}></i>
        </button>
      </div>
      <div className="navi-search-plus-setting">
        {type === "drive" && (
          <label>
            <input type="checkbox" name={driveHighwayType}
              checked={naviSearch.drive[driveHighwayType] === 2}
              onChange={ setHighwayCheck }
            ></input>
            <span>有料道路上に設定</span>
          </label>
        )}

        {!(name && lat && lon) && ( // 検索条件がそれってない場合のみ地図の中心を選択を出す
          <a onClick={setMapCenter} href="#">
            地図の中心を選択
          </a>
        )}
      </div>
    </div>
  );
});

export default NaviFrom;
